import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Icon } from '@mui/material';
import useStyles from './styles';
import { get, size, upperFirst, set, keyBy, debounce } from 'global/lib/lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';
import sendData from 'redux/actions/sendData';
import { Loader as Loading } from 'global/styles';
import SnackAlerts from 'global/components/snack-alerts';
import DataReports from './DataReports';
import clsx from 'clsx';
import { Header } from '../ReportList';
import { FilterChips } from 'global/components/filterchips';
import { Text } from 'global/components/text';
import { useChartSettings } from '../../../global/hooks/useChartSettings';
import { Row } from '../../../global/components/row';
import { Button } from '../../../global/components/Button';
import colors from '../../../global/styles/colors';
import { useUser } from '../../../global/hooks/useUser';
import { cleanChartFilters } from '../../../global/components/Charting';
import { useScreen } from '../../../global/styles';

const DATEFORMAT = 'MMM D, YYYY';
const CREATEFORMAT = 'YYYY-MM-DD HH:mm:ss';

const Loader = () => {
  return <Loading size={14} />;
};

export default function Template(props) {
  const classes = useStyles();

  const [alerts, setAlerts] = useState();
  const [loading, setLoading] = useState({
    assessments: { general: false, detailed: false },
    tasks: { general: false, detailed: false },
  });
  const [chartFilters, setChartFilters] = useState([]);
  const reportRef = useRef();

  const [state, setState] = useState({
    showSavedCharts: false,
    userGroups: [],
    load: -1,
  });

  const { vendors } = props;

  const filter = useSelector((state) => get(state, 'filter.reports.data'));
  const hasRealm = useSelector((state) => get(state, 'auth.realm'));
  const scorecards = useSelector((state) => get(state, 'data.scorecards'));
  const chartSettings = useChartSettings({ loadCharts: false });
  const { realm, chartRealm } = useUser();
  const { isMobile } = useScreen();

  const loadCharts = useCallback(debounce((cs, st, cr) => {
    const charts = cs.filterChartsByRealmTier(get(cr, 'tier'), cs.filterChartsByUserGroups(st.userGroups, cs.charts));
    const load = st.load + 1;
    setState((s) => ({ ...s, savedCharts: charts, load, showSavedCharts: load > 0 ? s.showSavedCharts : !!size(charts) }));
  }, 750, { leading: false, trailing: true }), []);

  useEffect(() => {
    loadCharts(chartSettings, state, chartRealm);
  }, [chartSettings.charts, chartRealm, state.userGroups]);

  useEffect(() => {
    if (!size(chartSettings.charts) || !state.showSavedCharts || !size(state.savedCharts)) {
      return;
    }

    const filters = cleanChartFilters(filter);

    chartSettings.loadCharts(filters.filter, state.savedCharts);
  }, [filter, chartSettings.charts, state.savedCharts]);

  const _scorecards = keyBy(scorecards, 'id');

  const vendor = get(vendors, 'data', []).find((v) => v.vendorId == get(filter, 'vendor_id'));

  const displayList = [
    { title: 'Location', value: (p) => get(hasRealm, 'name'), reportVar: 'realm_id', pureValue: () => get(hasRealm, 'id') },
    {
      title: 'Location Types',
      value: (p) =>
        size(get(filter, 'realm_type')) > 0 &&
        get(filter, 'realm_type')
          .map((t) => upperFirst(t))
          .join(', '),
      reportVar: 'realm_type',
      pureValue: () => get(filter, 'realm_type', []).join(','),
    },
    { title: 'Scorecard', value: (p) => get(_scorecards, [filter.scorecard_id, 'name']), reportVar: 'scorecard_id', pureValue: () => get(_scorecards, [filter.scorecard_id, 'id']) },
    {
      title: 'Start Date',
      value: (p) => get(filter, 'start_date') && moment(get(filter, 'start_date')).format(DATEFORMAT),
      reportVar: 'start_date',
      pureValue: () => moment(get(filter, 'start_date')).format(CREATEFORMAT),
    },
    {
      title: 'End Date',
      value: (p) => get(filter, 'end_date') && moment(get(filter, 'end_date')).format(DATEFORMAT),
      reportVar: 'end_date',
      pureValue: () => moment(get(filter, 'end_date')).format(CREATEFORMAT),
    },
    { title: 'Company', value: (p) => get(vendor, 'vendor.name'), reportVar: 'vendor_id', pureValue: () => get(vendor, 'vendorId') },
  ];

  const sendEvent = async (model_type, report_type) => {
    const attributes = (() => {
      const obj = {
        // 'scorecard_id': '',
        // 'realm_id': '',
        // 'realm_type': '',
        // 'user_id': '',
        // 'start_date': '',
        // 'end_date': '',
        model_type,
        report_type,
      };

      displayList.map((dl) => {
        const val = dl.value();
        if (val) {
          set(obj, dl.reportVar, dl.pureValue());
        }
      });

      return obj;
    })();

    const report = await sendData(
      {
        url: `/report-downloads`,
        method: 'post',
        data: {
          data: {
            type: 'report-downloads',
            attributes,
          },
        },
      },
      { normalize: { build: ['reportDownloads'] } }
    );

    return report;
  };

  const btnClick = async (eName) => {
    const spl = eName.split('_');
    try {
      setLoading((l) => {
        set(l, spl, true);
        return { ...l };
      });

      await sendEvent(...eName.split('_'));

      setLoading((l) => {
        set(l, spl, false);
        return { ...l };
      });

      setAlerts({ message: 'Your data is being processed. We’ll send you an email with the final results.' });
    } catch (err) {
      console.log(err.response);
      setAlerts({
        message: `Error: ${err.message}`,
        severity: 'error',
      });
      setLoading((l) => {
        set(l, spl, false);
        return { ...l };
      });
    }
  };

  const isLoading = (spl) => {
    const [model_type, report_type] = spl.split('_');
    return !!get(loading, [model_type, report_type]);
  };

  const onFilters = (chips) => {
    const arr = Object.keys(chips)
      .filter((k) => !!chips[k])
      .map((k) => ({ ...chips[k], key: k }));
    setChartFilters(arr);
  };

  const delFilter = (f) => {
    get(reportRef, 'current.removeFilter', () => 0)(f.key);
  };

  const ugroups = state.userGroups.map((u) => u.name).join(', ');

  const getUgDisplayText = () => {
    const rtext = realm ? `in ${realm.name}` : '';
    if (size(state.savedCharts)) {
      return `Showing default charts for ${ugroups} ${rtext}`;
    }
    return `Custom charts for ${ugroups} ${rtext}`;
  };

  return (
    <div className={classes.padLeft}>
      {!isMobile && <Text type="headline_medium">Charts</Text>}
      <Header {...props} title="" padLeft={false}>
        <div className={classes.filterBuffer}>
          <FilterChips filters={chartFilters} onDelete={delFilter} includeRealm={false} />
        </div>
      </Header>

      <Row spaceBetween>
        <Text>{getUgDisplayText()}</Text>
        <Button
          style={{ color: colors.blue }}
          onClick={() => setState((s) => ({ ...s, showSavedCharts: !s.showSavedCharts }))}
          variant="none"
          title={'CUSTOM CHARTS'}
          endIcon={<Icon>{state.showSavedCharts ? 'arrow_drop_down' : 'arrow_right'}</Icon>}
        />
      </Row>

      {!state.showSavedCharts && (
        <div className={classes.colTitle}>
          Choose "Row" and "Column" to create a score chart. <em>Some options are disabled for clarity.</em>
        </div>
      )}

      <DataReports ref={reportRef} vendors={vendors} onFilters={onFilters} chartSettings={chartSettings} {...state} updateUserGroups={(ug) => setState((s) => ({ ...s, userGroups: ug }))} />

      <Typography variant="h5" align="left">
        Downloads
      </Typography>
      <Header {...props} title="" padLeft={false}>
        <FilterChips filters={chartFilters} onDelete={delFilter} />
      </Header>

      <div className={clsx(classes.buttonContainer, classes.hidePrint)}>
        <div className={classes.btnCol}>
          <div className={classes.colTitle}>Assessments</div>
          <div className={classes.btnRow}>
            <Button
              startIcon={isLoading('assessments_overall') ? <Loader /> : <Icon>save_alt</Icon>}
              onClick={() => btnClick('assessments_overall')}
              variant={'outlined'}
              color={'secondary'}
              disabled={isLoading('assessments_overall')}
            >
              Overall Scores
            </Button>
            <Button
              startIcon={isLoading('assessments_general') ? <Loader /> : <Icon>save_alt</Icon>}
              onClick={() => btnClick('assessments_general')}
              variant={'outlined'}
              color={'secondary'}
              disabled={isLoading('assessments_general')}
            >
              Systems Scores
            </Button>
            <Button
              startIcon={isLoading('assessments_detailed') ? <Loader /> : <Icon>save_alt</Icon>}
              onClick={() => btnClick('assessments_detailed')}
              variant={'outlined'}
              color={'secondary'}
              disabled={isLoading('assessments_detailed')}
            >
              Detailed Scores
            </Button>
          </div>
        </div>

        <div className={classes.btnCol}>
          <div className={classes.colTitle}>Tasks</div>
          <div className={classes.btnRow}>
            <Button
              startIcon={isLoading('tasks_general') ? <Loader /> : <Icon>save_alt</Icon>}
              onClick={() => btnClick('tasks_general')}
              variant={'outlined'}
              color={'secondary'}
              disabled={isLoading('tasks_general')}
            >
              Tasks List
            </Button>
            <Button
              startIcon={isLoading('tasks_detailed') ? <Loader /> : <Icon>save_alt</Icon>}
              onClick={() => btnClick('tasks_detailed')}
              variant={'outlined'}
              color={'secondary'}
              disabled={isLoading('tasks_detailed')}
            >
              Tasks detailed
            </Button>
          </div>
        </div>
      </div>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
}
